import * as Sentry from "@sentry/vue";
import DossierContactClient from "@/dossier/client/DossierContactClient";
import { DossierId } from "@/dossier/models/AbstractDossier";
import DmsService from "@/dossier/services/DmsService";
import { removeById } from "@/shared/utils";
import ContactContext from "../models/ContactContext";
import ContactData from "../models/ContactData";
import DossierContact, { DossierContactId } from "../models/DossierContact";

class DossierContactService {
  async loadAllForDossier(dossierId: DossierId): Promise<ContactContext> {
    const contacts = await DossierContactClient.loadByDossierId(dossierId);
    return new ContactContext(dossierId, contacts);
  }

  async save(
    data: ContactData,
    context: ContactContext,
    contact?: DossierContact
  ): Promise<void> {
    if (contact) {
      await this.update(contact, data, context);
    } else {
      await this.create(data, context);
    }
  }

  async create(data: ContactData, context: ContactContext): Promise<void> {
    const contact = await DossierContactClient.create(data, context.dossierId);
    context.mergeContact(contact);
  }

  async update(
    contact: DossierContact,
    data: ContactData,
    context: ContactContext
  ): Promise<void> {
    const updatedContact = await DossierContactClient.update(
      contact.id,
      data,
      context.dossierId
    );
    context.mergeContact(updatedContact);
  }

  async remove(id: DossierContactId, context: ContactContext): Promise<void> {
    await DossierContactClient.remove(id, context.dossierId);
    removeById(context.contacts, id);
  }

  async dmsSync(context: ContactContext): Promise<void> {
    try {
      const dmsStatus = await DossierContactClient.dmsSync(context.dossierId);
      await DmsService.processSyncWithStatus(dmsStatus);
      const contacts = await DossierContactClient.loadByDossierId(
        context.dossierId
      );
      context.setContacts(contacts);
    } catch (err) {
      Sentry.captureException(err, {
        level: "error",
        tags: { section: "dms", method: "dmsSync" },
        extra: { dossierId: context?.dossierId },
      });
      throw err;
    }
  }
}

export default new DossierContactService();
